// Kleine API-helper: JSON in en uit (of een formulier met bestanden), fouten als leesbare tekst. Bij 401 stuurt de app naar login.

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** Het hele foutantwoord van de server (bijvoorbeeld bij een geweigerd bestand: soort, kenmerk en plaats), of null. */
    public readonly data: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Een formulier (bestanden): de browser zet zelf het type met de grens. Gaat voor `body`. */
  form?: FormData;
  signal?: AbortSignal;
  /** Achtergrondwerk (vooruit ophalen, verversen): de server geeft het bij de connector minder voorrang dan wat Jeroen zelf doet. */
  background?: boolean;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** Meekijker voor signalen (trage aanroepen, fouten); wordt door lib/signals.ts gezet, zodat dit bestand geen afhankelijkheden krijgt. */
export interface ApiObserver {
  onError(method: string, path: string, status: number, message: string): void;
  onSlow(method: string, path: string, ms: number): void;
}
export const SLOW_MS = 4000;
let observer: ApiObserver | null = null;
export function setApiObserver(o: ApiObserver | null): void {
  observer = o;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const init: RequestInit = { method: opts.method ?? 'GET', headers: {}, credentials: 'same-origin' };
  if (opts.signal) init.signal = opts.signal;
  if (opts.background) (init.headers as Record<string, string>)['x-jerome-lane'] = 'background';
  if (opts.form) init.body = opts.form;
  else if (opts.body !== undefined) {
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  const method = opts.method ?? 'GET';
  const started = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const r = await fetch(path, init);
  const took = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started;
  if (took > SLOW_MS) observer?.onSlow(method, path, took);
  if (r.status === 401) {
    onUnauthorized?.();
    throw new ApiError('niet ingelogd', 401);
  }
  if (r.status === 204) return undefined as T;
  const text = await r.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!r.ok) {
    const message = (data as { error?: string } | null)?.error ?? `de server gaf status ${r.status}`;
    observer?.onError(method, path, r.status, message);
    throw new ApiError(message, r.status, data && typeof data === 'object' ? (data as Record<string, unknown>) : null);
  }
  return data as T;
}

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}
